import { useLocalStore } from 'mobx-react';
import { action, runInAction } from 'mobx';
import { updatePassword, updatePasswordByStaff } from 'Services/Api/AccountService';
import alertToast from 'Util/ToastifyUtils';
import { UpdatePasswordLocationState } from 'Views/Pages/UpdatePasswordPage';
import { PasswordOptions } from 'Global';
import { validatePassword } from 'Validators/Functions/HumanWritten/Password';
import { stringNotEmpty } from 'Util/TypeGuards';

interface IUpdatePasswordStateErrors {
	password?: string;
	confirmPassword?: string;
	[attr: string]: string | undefined;
}

interface IUpdatePasswordState {
	loading: boolean;
	password: string;
	confirmPassword: string;
	errors: IUpdatePasswordStateErrors;
}

export function useUpdatePassword(options: PasswordOptions, userInfo?: UpdatePasswordLocationState) {
	const state = useLocalStore<IUpdatePasswordState>(() => ({
		loading: false,
		password: '',
		confirmPassword: '',
		errors: {},
	}));

	// Event-free so the page can drive it from the header / mobile save bar, which sit outside
	// the form. The form's own submit handler wraps it to keep Enter-to-submit working.
	const submitPassword = action(async () => {
		if (state.loading) {
			return;
		}

		const errors: IUpdatePasswordStateErrors = {};

		const passwordError = validatePassword(options, state.password);
		if (!state.password) {
			errors.password = 'Required';
		} else if (stringNotEmpty(passwordError)) {
			errors.password = passwordError;
		} else if (!state.confirmPassword) {
			errors.confirmPassword = 'Required';
		} else if (state.confirmPassword !== state.password) {
			errors.confirmPassword = 'Does not match';
		}

		state.errors = errors;

		if (Object.keys(state.errors).length === 0) {
			try {
				runInAction(() => {
					state.loading = true;
				});
				if (userInfo) {
					// When userInfo is provided, it means it is
					// a staff user editing a customer's password
					await updatePasswordByStaff({
						newPassword: state.password,
						username: userInfo.email,
					});
				} else {
					await updatePassword(state.password);
				}
				alertToast('Successfully updated password', 'success');
				runInAction(() => {
					state.loading = false;
				});
				window.history.back();
			} catch {
				alertToast('Failed to update password', 'error');
				runInAction(() => {
					state.loading = false;
				});
			}
		}
	});

	const onUpdatePasswordClicked = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		return submitPassword();
	};

	const onAfterChange = action((key: keyof IUpdatePasswordStateErrors) => {
		state.errors[key] = '';
	});

	return {
		state,
		submitPassword,
		onUpdatePasswordClicked,
		onAfterChange,
	};
}
